import { runDebtCalculation } from '../features/debt-calculator/lib/runDebtCalculation'
import { SharedProfile, toDebtProfile } from '../lib/sharedProfile'
import { MatchedScholarship } from '../features/scholarships/types'

interface Props {
    profile: SharedProfile
    scholarships: MatchedScholarship[]
}

export default function CombinedResultsPage({ profile, scholarships }: Props) {
    const result = runDebtCalculation(toDebtProfile(profile))

    const comparison = result.comparison as any
    const oldGrants = comparison?.pre2026?.grants ?? 0
    const newGrants = comparison?.post2026?.grants ?? 0
    const grantGap = Math.max(oldGrants - newGrants, 0)

    const scholarshipTotal = scholarships.reduce((sum, s) => {
        const n = parseInt(s.amount?.replace(/[^0-9]/g, '') || '0')
        return sum + (isNaN(n) ? 0 : n)
    }, 0)

    const gapCovered = Math.min(scholarshipTotal, grantGap)
    const stillExposed = Math.max(grantGap - scholarshipTotal, 0)
    const coveragePercent =
        grantGap > 0
            ? Math.min(Math.round((gapCovered / grantGap) * 100), 100)
            : 0

    return (
        <div className="max-w-2xl mx-auto p-6 space-y-6">

            <div className="rounded-2xl bg-red-50 border border-red-100 p-6 text-center">
                <p className="text-sm text-red-500 font-medium mb-1">
                    The 2026 OSAP cuts will cost you
                </p>
                <p className="text-5xl font-bold text-red-600">
                    ${grantGap.toLocaleString()}
                </p>
                <p className="text-sm text-red-400 mt-1">
                    in grants you would have received under the old system
                </p>
            </div>

            <div className="rounded-2xl border p-5">
                <h2 className="font-semibold mb-4">Your OSAP: Before vs After 2026</h2>
                <div className="grid grid-cols-2 gap-4">
                    <div className="text-center p-4 bg-green-50 rounded-xl">
                        <p className="text-xs text-gray-500 mb-1">Before 2026</p>
                        <p className="text-2xl font-bold text-green-600">
                            ${oldGrants.toLocaleString()}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">in grants</p>
                    </div>
                    <div className="text-center p-4 bg-red-50 rounded-xl">
                        <p className="text-xs text-gray-500 mb-1">Fall 2026 onwards</p>
                        <p className="text-2xl font-bold text-red-500">
                            ${newGrants.toLocaleString()}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">in grants</p>
                    </div>
                </div>
            </div>

            <div className="rounded-2xl border p-5">
                <h2 className="font-semibold mb-4">Can Scholarships Close the Gap?</h2>
                <div className="mb-5">
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                        <span>Gap covered by scholarships</span>
                        <span>{coveragePercent}%</span>
                    </div>
                    <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-blue-500 rounded-full transition-all duration-700"
                            style={{ width: `${coveragePercent}%` }}
                        />
                    </div>
                </div>
                <div className="grid grid-cols-3 gap-3 text-center">
                    <div>
                        <p className="text-xl font-bold text-gray-800">
                            ${grantGap.toLocaleString()}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">Funding gap</p>
                    </div>
                    <div>
                        <p className="text-xl font-bold text-blue-600">
                            ${gapCovered.toLocaleString()}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">Covered</p>
                    </div>
                    <div>
                        <p className="text-xl font-bold text-red-500">
                            ${stillExposed.toLocaleString()}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">Still exposed</p>
                    </div>
                </div>
            </div>

            <div>
                <h2 className="font-semibold mb-3">
                    {scholarships.length} Scholarships Found
                </h2>
                {scholarships.length === 0 ? (
                    <p className="text-gray-500 text-sm">
                        No scholarships matched your exact profile.
                    </p>
                ) : (
                    <div className="space-y-3">
                        {scholarships.map((s, i) => (
                            <div key={i} className="border rounded-xl p-4">
                                <div className="flex justify-between items-start">
                                    <h3 className="font-medium">{s.name}</h3>
                                    <span className="text-green-600 font-bold text-sm ml-4">
                    {s.amount}
                  </span>
                                </div>
                                {s.matchReason && (
                                    <p className="text-sm text-gray-500 mt-1">{s.matchReason}</p>
                                )}
                                <div className="flex justify-between items-center mt-2">
                  <span className="text-xs text-gray-400">
                    Deadline: {s.deadline || 'Check site'}
                  </span>
                                    {s.link && (
                                        <a
                                            href={s.link}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-blue-500 text-xs hover:underline"
                                        >
                                            Apply
                                        </a>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <p className="text-xs text-amber-600 text-center pb-6">
                All figures are estimates. Verify OSAP eligibility and scholarship
                amounts directly before applying.
            </p>

        </div>
    )
}